import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { useMfaGate } from "@/hooks/use-mfa-gate";
import { DesafioMfa } from "@/components/DesafioMfa";
import { usePlan } from "@/hooks/usePlan";
import { TravaPeriodoGratis } from "@/components/TravaPeriodoGratis";
import { AutenticacaoIndisponivel } from "@/components/AutenticacaoIndisponivel";
import { SubscriptionProvider } from "@/hooks/use-subscription";
import { RoleProvider, useRole } from "@/hooks/use-role";
import { ActivePanelProvider, useActivePanel } from "@/hooks/use-active-panel";
import { ROTAS_DO_ANUNCIANTE } from "@/config/navigation";
import AdminRoute from "@/components/AdminRoute";
import SuperAdminRoute from "@/components/SuperAdminRoute";
import AdminLayout from "@/components/admin/AdminLayout";
import SuperAdminLayout from "@/components/superadmin/SuperAdminLayout";
import AppLayout from "@/components/AppLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { FlagGate } from "@/components/FlagGate";

// Lazy-loaded pages — cada rota vira um chunk separado
const Dashboard         = lazy(() => import("@/pages/Dashboard"));
const Imoveis           = lazy(() => import("@/pages/Imoveis"));
const Contratos         = lazy(() => import("@/pages/Contratos"));
const ContratoNovo      = lazy(() => import("@/pages/ContratoNovo"));
const Inquilinos        = lazy(() => import("@/pages/Inquilinos"));
const Cobrancas         = lazy(() => import("@/pages/Cobrancas"));
const AlertasPage       = lazy(() => import("@/pages/AlertasPage"));
const Relatorios        = lazy(() => import("@/pages/Relatorios"));
const Fiscal            = lazy(() => import("@/pages/Fiscal"));
const FiscalIbsCbs      = lazy(() => import("@/pages/FiscalIbsCbs"));
const RelatorioIR       = lazy(() => import("@/pages/RelatorioIR"));
const Analytics         = lazy(() => import("@/pages/Analytics"));
const Notificacoes      = lazy(() => import("@/pages/Notificacoes"));
const Planos            = lazy(() => import("@/pages/Planos"));
const PlanosV2          = lazy(() => import("@/pages/PlanosV2"));
const PlanosV3          = lazy(() => import("@/pages/PlanosV3"));
const Assinatura        = lazy(() => import("@/pages/Assinatura"));
const Configuracoes       = lazy(() => import("@/pages/Configuracoes"));
const ConfiguracoesEquipe      = lazy(() => import("@/pages/ConfiguracoesEquipe"));
const ConfiguracoesApi         = lazy(() => import("@/pages/ConfiguracoesApi"));
const ApiDocs                  = lazy(() => import("@/pages/ApiDocs"));
const ConfiguracoesWhiteLabel  = lazy(() => import("@/pages/ConfiguracoesWhiteLabel"));
const Indicar           = lazy(() => import("@/pages/Indicar"));
// As quatro telas do GIFI Anunciar deixaram de ser importadas em 09/09/2026: o
// módulo foi aposentado e as rotas redirecionam. Os arquivos ficam no
// repositório — apagá-los junto com a decisão tiraria a chance de consultar o
// que existia —, e sem import eles saem do pacote, que é o efeito desejado.
// GIFI Vitrine — classificados. Produto distinto do Anunciar (que publica imóvel
// para locação): tabelas, rotas e créditos próprios, sobre o mesmo motor de slots.
const VitrineLista        = lazy(() => import("@/pages/VitrineLista"));
const VitrineDetalhe      = lazy(() => import("@/pages/VitrineDetalhe"));
const VitrineMeusAnuncios = lazy(() => import("@/pages/VitrineMeusAnuncios"));
const VitrineNovo         = lazy(() => import("@/pages/VitrineNovo"));
const VitrineCreditos     = lazy(() => import("@/pages/VitrineCreditos"));
const VitrineLeads        = lazy(() => import("@/pages/VitrineLeads"));
const SuperAdminVitrine   = lazy(() => import("@/pages/superadmin/SuperAdminVitrine"));
const AlugarPublico     = lazy(() => import("@/pages/AlugarPublico"));
const EspelhoLanding    = lazy(() => import("@/pages/EspelhoLanding"));
const ImovelConectores  = lazy(() => import("@/pages/ImovelConectores"));
const ImovelFinanceiro  = lazy(() => import("@/pages/ImovelFinanceiro"));
const ImovelDados       = lazy(() => import("@/pages/ImovelDados"));
const Conectores        = lazy(() => import("@/pages/Conectores"));
const Login             = lazy(() => import("@/pages/Login"));
const ResetPassword     = lazy(() => import("@/pages/ResetPassword"));
const Privacidade       = lazy(() => import("@/pages/Privacidade"));
const Termos            = lazy(() => import("@/pages/Termos"));
const SolicitacaoLgpd   = lazy(() => import("@/pages/SolicitacaoLgpd"));
const Seguranca         = lazy(() => import("@/pages/Seguranca"));
const AcceptInvite      = lazy(() => import("@/pages/AcceptInvite"));
const NotFound          = lazy(() => import("@/pages/NotFound"));
const ConectarGmail     = lazy(() => import("@/pages/ConectarGmail"));
const Tutoriais           = lazy(() => import("@/pages/Tutoriais"));
const ReferralRedirect    = lazy(() => import("@/pages/ReferralRedirect"));
const PortalInquilino     = lazy(() => import("@/pages/PortalInquilino"));
const MapaPortfolio       = lazy(() => import("@/pages/MapaPortfolio"));
const ParceirosCadastro   = lazy(() => import("@/pages/ParceirosCadastro"));
const ParceiroPedidos     = lazy(() => import("@/pages/ParceiroPedidos"));

// Admin pages
const AdminOverview            = lazy(() => import("@/pages/admin/AdminOverview"));
const AdminCustomers           = lazy(() => import("@/pages/admin/AdminCustomers"));
const AdminCustomerDetail      = lazy(() => import("@/pages/admin/AdminCustomerDetail"));
const AdminSubscriptions       = lazy(() => import("@/pages/admin/AdminSubscriptions"));
const AdminSubscriptionDetail  = lazy(() => import("@/pages/admin/AdminSubscriptionDetail"));
const AdminPlans               = lazy(() => import("@/pages/admin/AdminPlans"));
const AdminCoupons             = lazy(() => import("@/pages/admin/AdminCoupons"));
const AdminUsers               = lazy(() => import("@/pages/admin/AdminUsers"));
const AdminAuditLogs           = lazy(() => import("@/pages/admin/AdminAuditLogs"));
const AdminWebhookLogs         = lazy(() => import("@/pages/admin/AdminWebhookLogs"));

// SuperAdmin pages
const SuperAdminOverview           = lazy(() => import("@/pages/superadmin/SuperAdminOverview"));
const SuperAdminServices           = lazy(() => import("@/pages/superadmin/SuperAdminServices"));
const SuperAdminCosts              = lazy(() => import("@/pages/superadmin/SuperAdminCosts"));
const SuperAdminFlags              = lazy(() => import("@/pages/superadmin/SuperAdminFlags"));
const SuperAdminLogs               = lazy(() => import("@/pages/superadmin/SuperAdminLogs"));
const SuperAdminUsers              = lazy(() => import("@/pages/superadmin/SuperAdminUsers"));
const SuperAdminEmailTemplates     = lazy(() => import("@/pages/superadmin/SuperAdminEmailTemplates"));
const SuperAdminPlans              = lazy(() => import("@/pages/superadmin/SuperAdminPlans"));
const SuperAdminNotificationTypes  = lazy(() => import("@/pages/superadmin/SuperAdminNotificationTypes"));
const SuperAdminMessaging          = lazy(() => import("@/pages/superadmin/SuperAdminMessaging"));
const SuperAdminReset              = lazy(() => import("@/pages/superadmin/SuperAdminReset"));
const SuperAdminInfrastructure     = lazy(() => import("@/pages/superadmin/SuperAdminInfrastructure"));
const SuperAdminCompliance         = lazy(() => import("@/pages/superadmin/SuperAdminCompliance"));
const SuperAdminAnunciar           = lazy(() => import("@/pages/superadmin/SuperAdminAnunciar"));
const SuperAdminPartners           = lazy(() => import("@/pages/superadmin/SuperAdminPartners"));
const SuperAdminCondominios        = lazy(() => import("@/pages/superadmin/SuperAdminCondominios"));
const SuperAdminFiscal             = lazy(() => import("@/pages/superadmin/SuperAdminFiscal"));
const SuperAdminCompAccounts       = lazy(() => import("@/pages/superadmin/SuperAdminCompAccounts"));
const SuperAdminTutoriais          = lazy(() => import("@/pages/superadmin/SuperAdminTutoriais"));
const SuperAdminWhiteLabel         = lazy(() => import("@/pages/superadmin/SuperAdminWhiteLabel"));
const SuperAdminCredentials        = lazy(() => import("@/pages/superadmin/SuperAdminCredentials"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  },
});

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
  </div>
);

function HomeRoute() {
  const { user, loading, indisponivel } = useAuth();
  const { isSuperAdmin, isAdmin, loading: roleLoading } = useRole();
  const { activePanel, panelReady } = useActivePanel();
  if (loading || roleLoading || !panelReady) return <PageLoader />;
  // Quem tem sessão guardada e não pôde validá-la não é visitante: mostrar a
  // página de vendas para essa pessoa seria trocar um erro por um mal-entendido.
  // Visitante de verdade não passa por aqui — sem sessão guardada, `getSession`
  // responde na hora e `indisponivel` nunca fica verdadeiro.
  if (indisponivel && !user) return <AutenticacaoIndisponivel />;
  if (!user) return (
    <Suspense fallback={<PageLoader />}>
      <EspelhoLanding />
    </Suspense>
  );
  if (isSuperAdmin && activePanel !== "user") return <Navigate to="/superadmin" replace />;
  if (isAdmin && activePanel !== "user") return <Navigate to="/admin" replace />;
  return (
    <AppLayout>
      <Suspense fallback={<PageLoader />}>
        <Dashboard />
      </Suspense>
    </AppLayout>
  );
}

/**
 * A lista da Vitrine, para quem tem conta e para quem não tem.
 *
 * Ela vivia dentro de `ProtectedRoute`, e o efeito era um classificado que
 * ninguém de fora conseguia percorrer: só a página de UM anúncio era pública, e
 * chegar até ela dependia de já ter o link. Vitrine que exige cadastro para ser
 * olhada não atrai comprador nem anunciante.
 *
 * O padrão é o do `HomeRoute` logo acima: visitante recebe a tela nua, quem tem
 * sessão recebe a mesma tela dentro do `AppLayout` — que escolhe o menu pelo
 * painel ativo, então anunciante e proprietário continuam vendo o que viam.
 *
 * `ClientGuard` deixa de cobrir esta rota, e é o certo: ele é guarda de
 * NAVEGAÇÃO, serve para não largar alguém numa tela que não é do painel dele. A
 * Vitrine é de todos os painéis — o próprio menu do Anunciante tem "Ver a
 * Vitrine" apontando para cá.
 */
function VitrineRoute() {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  const lista = (
    <Suspense fallback={<PageLoader />}>
      <VitrineLista />
    </Suspense>
  );
  return user ? <AppLayout>{lista}</AppLayout> : lista;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading, indisponivel } = useAuth();
  // O desafio mora aqui, e não no Login, porque a sessão também nasce do OAuth
  // do Google e da volta de um link por e-mail. Um portão por porta de entrada
  // é um portão esquecido a cada porta nova.
  const { carregando: mfaCarregando, precisaElevar, revalidar } = useMfaGate();
  // A trava do Free pós-período-gratuito (decidido em 02/10) é a mesma classe
  // de portão: só se vê aqui, só vale no banco. `isSuperAdmin`/`isAdmin` nunca
  // travam — o plano é um conceito de cliente, e essas contas não passam pelo
  // checkout comercial.
  const { isSuperAdmin, isAdmin, loading: roleLoading } = useRole();
  const { periodoGratisExpirado, carregandoLimites } = usePlan();

  if (loading || (user && mfaCarregando) || (user && roleLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }
  // Antes do redirecionamento para o login: sem resposta do serviço, não dá para
  // afirmar que a pessoa NÃO está autenticada. Mandá-la ao login diria uma coisa
  // que não se sabe, e ela tentaria entrar num serviço que está fora.
  if (indisponivel && !user) return <AutenticacaoIndisponivel />;
  if (!user) return <Navigate to="/login" replace />;
  if (precisaElevar) return <DesafioMfa aoElevar={revalidar} />;
  // "Ainda não sei" nunca é "trave" — só decide depois que a RPC de limites
  // respondeu, e só para quem não é admin/superadmin.
  if (!isSuperAdmin && !isAdmin && !carregandoLimites && periodoGratisExpirado) {
    return <TravaPeriodoGratis />;
  }
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const { isSuperAdmin, isAdmin, loading: roleLoading } = useRole();

  if (loading || roleLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }
  if (user) {
    if (isSuperAdmin) return <Navigate to="/superadmin" replace />;
    if (isAdmin) return <Navigate to="/admin" replace />;
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function ClientGuard({ children }: { children: React.ReactNode }) {
  const { isSuperAdmin, isAdmin, loading: roleLoading } = useRole();
  const { activePanel, panelReady } = useActivePanel();
  const { pathname } = useLocation();
  if (roleLoading || !panelReady) return <PageLoader />;
  if (isSuperAdmin && activePanel !== "user" && activePanel !== "partner") return <Navigate to="/superadmin" replace />;
  if (isAdmin && activePanel !== "user" && activePanel !== "partner") return <Navigate to="/admin" replace />;

  // No painel Anunciante, as telas de gestão de imóveis não são oferecidas —
  // e também não são alcançáveis por URL digitada. Isto NÃO é permissão: quem
  // está aqui pode voltar ao painel Cliente pelo seletor a qualquer momento, e
  // o que a pessoa pode LER continua decidido pela RLS do banco. É navegação:
  // manter a pessoa dentro do contexto que ela escolheu, em vez de largá-la
  // numa tela vazia que fala de contratos que ela não tem.
  if (activePanel === "partner" && !rotaDoAnunciante(pathname)) {
    return <Navigate to="/vitrine/meus-anuncios" replace />;
  }
  return <>{children}</>;
}

/**
 * A rota pertence ao painel Anunciante?
 *
 * Compara por caminho INTEIRO, nunca por prefixo solto: `startsWith("/vitrine")`
 * deixaria passar qualquer coisa que comece assim, e prefixo frouxo já foi
 * defeito neste repositório. As rotas com parâmetro entram pelo seu prefixo
 * declarado, uma a uma.
 */
function rotaDoAnunciante(pathname: string): boolean {
  const limpo = pathname.replace(/\/+$/, "") || "/";
  if ((ROTAS_DO_ANUNCIANTE as readonly string[]).includes(limpo)) return true;
  // A página pública de um anúncio: /vitrine/<categoria>/<slug>
  return /^\/vitrine\/[^/]+\/[^/]+$/.test(limpo);
}

/**
 * Segundo limite de erro, por ÁREA de rota.
 *
 * O limite de fora (em `main.tsx`) existe para que exceção em provider não vire
 * tela branca; ele é a última rede e, quando dispara, a pessoa perde a
 * aplicação inteira. Este aqui fica DENTRO do `AppLayout`: erro numa página do
 * cliente derruba o conteúdo e mantém menu, cabeçalho e navegação — dá para sair
 * da tela quebrada em vez de recarregar tudo.
 *
 * A `key` é o caminho: sem ela o boundary guarda `hasError: true` para sempre e
 * navegar para outra rota continuaria mostrando o erro da anterior.
 */
const AreaDeRotaDoCliente = ({ children }: { children: React.ReactNode }) => {
  const { pathname } = useLocation();
  return <ErrorBoundary key={pathname}>{children}</ErrorBoundary>;
};

/**
 * O portão de sessão como rota-MÃE, e não como embrulho de tudo.
 *
 * Ele envolvia o `<Routes>` inteiro, então decidia ANTES de o roteador saber se
 * o endereço existe. Efeito medido na produção, abrindo a tela: um visitante em
 * `/rota-que-nao-existe` recebia a **tela de login** — nunca a 404. E o
 * `ProtectedRoute` não guarda o destino, então entrar levava ao Dashboard: a
 * pessoa nunca ficava sabendo que a página não existia.
 *
 * Como rota-mãe, o portão cobre exatamente as rotas do cliente, e o catch-all
 * fica IRMÃO dele, fora do portão. **"Não existe" é a mesma resposta para quem
 * entrou e para quem não entrou** — condicioná-la à sessão faz o visitante
 * concluir que precisa de conta para ver algo que simplesmente não há, e é
 * exatamente quem chega por um link de campanha quebrado que paga isso.
 *
 * A correção anterior (ligar a 404 em rota) resolveu o miolo vazio para quem
 * tem sessão e parou ali. Metade de um caminho não é um caminho.
 */
const LayoutDoCliente = () => (
  <ProtectedRoute>
    <ClientGuard>
      <AppLayout>
        <AreaDeRotaDoCliente>
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
        </AreaDeRotaDoCliente>
      </AppLayout>
    </ClientGuard>
  </ProtectedRoute>
);

const AppRoutes = () => (
          <Routes>
            <Route element={<LayoutDoCliente />}>
            <Route path="/imoveis" element={<Imoveis />} />
            <Route path="/conectores" element={<Conectores />} />
            <Route path="/imovel/:imovelId/financeiro" element={<ImovelFinanceiro />} />
            <Route path="/imovel/:imovelId/gastos" element={<ImovelConectores />} />
            <Route path="/imovel/:imovelId/imovel" element={<ImovelDados />} />
            <Route path="/imovel/:imovelId/conectores" element={<ImovelConectores />} />
            <Route path="/contratos" element={<Contratos />} />
            <Route path="/contratos/novo" element={<ContratoNovo />} />
            <Route path="/contratos/:id/editar" element={<ContratoNovo />} />
            <Route path="/inquilinos" element={<Inquilinos />} />
            <Route path="/cobrancas" element={<Cobrancas />} />
            <Route path="/alertas" element={<AlertasPage />} />
            <Route path="/relatorios" element={<Relatorios />} />
            <Route path="/relatorio-ir" element={<RelatorioIR />} />
            <Route path="/fiscal" element={<Fiscal />} />
            <Route path="/fiscal/ibs-cbs" element={<FlagGate flag="ibs_cbs_enrollment_enabled" fallback="/fiscal" loading={<PageLoader />}><FiscalIbsCbs /></FlagGate>} />
            <Route path="/analytics" element={<Suspense fallback={<PageLoader />}><Analytics /></Suspense>} />
            <Route path="/notificacoes" element={<Notificacoes />} />
            <Route path="/planos" element={<Planos />} />
            {/* Escadas alternativas de preço: só abrem com a bandeira ligada. Antes eram
                abertas a qualquer logado, com limites que o banco não aplica. */}
            <Route path="/planos-v2" element={<FlagGate flag="pricing_v2_enabled" fallback="/planos" loading={<PageLoader />}><PlanosV2 /></FlagGate>} />
            <Route path="/planos-v3" element={<FlagGate flag="pricing_v3_enabled" fallback="/planos" loading={<PageLoader />}><PlanosV3 /></FlagGate>} />
            <Route path="/assinatura" element={<Assinatura />} />
            <Route path="/configuracoes" element={<Configuracoes />} />
            <Route path="/configuracoes/equipe" element={<Suspense fallback={<PageLoader />}><ConfiguracoesEquipe /></Suspense>} />
            <Route path="/configuracoes/api" element={<Suspense fallback={<PageLoader />}><ConfiguracoesApi /></Suspense>} />
            <Route path="/configuracoes/white-label" element={<Suspense fallback={<PageLoader />}><ConfiguracoesWhiteLabel /></Suspense>} />
            <Route path="/indicar" element={<Indicar />} />
            {/* GIFI Anunciar, aposentado em 09/09/2026. As telas e as tabelas
                ficam — o histórico é de quem usou —, mas o caminho fecha: quem
                chegar por link antigo vai para a Vitrine, que faz o mesmo
                trabalho com moderação, cota de plano e lote com validade. O
                motivo está em src/content/help/scope.ts. */}
            <Route path="/anunciar/*" element={<Navigate to="/vitrine" replace />} />
            <Route path="/anunciar" element={<Navigate to="/vitrine" replace />} />
            <Route path="/vitrine/meus-anuncios" element={<VitrineMeusAnuncios />} />
            <Route path="/vitrine/novo" element={<VitrineNovo />} />
            <Route path="/vitrine/creditos" element={<VitrineCreditos />} />
            <Route path="/vitrine/leads" element={<VitrineLeads />} />
            {/* `/vitrine` saiu daqui: é pública agora, montada por VitrineRoute. */}
            <Route path="/tutoriais" element={<Tutoriais />} />
            <Route path="/mapa" element={<MapaPortfolio />} />
            <Route path="/parceiro/pedidos" element={<ParceiroPedidos />} />
            </Route>
            {/* A tela 404 estava IMPORTADA e em rota nenhuma — o ESLint a
                acusava como valor não usado, e o efeito era pior que um 404
                feio: endereço desconhecido caía aqui, nenhuma rota casava, e o
                `<Routes>` renderizava NADA. A pessoa via o menu e o cabeçalho
                em volta de um miolo vazio, sem uma palavra explicando. É a
                mesma família da tela branca do provider: falha silenciosa que
                não distingue "não existe" de "não carregou".

                Ela fica FORA de `<Route element={<LayoutDoCliente/>}>` de
                propósito — ver o cabeçalho daquele componente. Dentro, o portão
                de sessão respondia antes, e o visitante recebia o login no
                lugar da 404. */}
            <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFound /></Suspense>} />
          </Routes>
);

const AdminRoutes = () => (
  <ProtectedRoute>
    <AdminRoute>
      <AdminLayout />
    </AdminRoute>
  </ProtectedRoute>
);

const SuperAdminRoutes = () => (
  <ProtectedRoute>
    <SuperAdminRoute>
      <SuperAdminLayout />
    </SuperAdminRoute>
  </ProtectedRoute>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <RoleProvider>
            <ActivePanelProvider>
            <SubscriptionProvider>
              {/* Este boundary NÃO cobre os providers acima dele — um limite de
                  erro só pega o que está abaixo. Quem cobre os sete providers é
                  o de `main.tsx`, que envolve o `<App/>` inteiro. Este fica para
                  o erro de rota, que é o caso comum e o mais barato de conter. */}
              <ErrorBoundary>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                  {/* Públicas e sem PublicRoute de propósito: quem já tem conta
                      também precisa reler o que aceitou, e quem ainda não tem
                      precisa ler antes de decidir. */}
                  <Route path="/privacidade" element={<Privacidade />} />
                  <Route path="/termos" element={<Termos />} />
                  <Route path="/solicitacao-lgpd" element={<SolicitacaoLgpd />} />
                  <Route path="/seguranca" element={<Seguranca />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/convite" element={<AcceptInvite />} />
                  <Route path="/conectar-gmail" element={<ConectarGmail />} />
                  <Route path="/admin" element={<AdminRoutes />}>
                    <Route index element={<AdminOverview />} />
                    <Route path="usuarios" element={<AdminUsers />} />
                    <Route path="clientes" element={<AdminCustomers />} />
                    <Route path="clientes/:customerId" element={<AdminCustomerDetail />} />
                    <Route path="assinaturas" element={<AdminSubscriptions />} />
                    <Route path="assinaturas/:subscriptionId" element={<AdminSubscriptionDetail />} />
                    <Route path="planos" element={<AdminPlans />} />
                    <Route path="cupons" element={<AdminCoupons />} />
                    <Route path="audit-logs" element={<Suspense fallback={<PageLoader />}><AdminAuditLogs /></Suspense>} />
                    <Route path="webhook-logs" element={<Suspense fallback={<PageLoader />}><AdminWebhookLogs /></Suspense>} />
                  </Route>
                  <Route path="/superadmin" element={<SuperAdminRoutes />}>
                    <Route index element={<SuperAdminOverview />} />
                    <Route path="servicos" element={<SuperAdminServices />} />
                    <Route path="custos" element={<SuperAdminCosts />} />
                    <Route path="feature-flags" element={<SuperAdminFlags />} />
                    <Route path="credenciais" element={<SuperAdminCredentials />} />
                    <Route path="logs" element={<SuperAdminLogs />} />
                    <Route path="users" element={<SuperAdminUsers />} />
                    <Route path="email-templates" element={<SuperAdminEmailTemplates />} />
                    <Route path="plans" element={<SuperAdminPlans />} />
                    <Route path="fiscal" element={<SuperAdminFiscal />} />
                    <Route path="notification-types" element={<SuperAdminNotificationTypes />} />
                    <Route path="messaging" element={<SuperAdminMessaging />} />
                    <Route path="reset" element={<SuperAdminReset />} />
                    <Route path="infrastructure" element={<SuperAdminInfrastructure />} />
                    <Route path="compliance" element={<SuperAdminCompliance />} />
                    <Route path="anunciar" element={<SuperAdminAnunciar />} />
                    <Route path="vitrine" element={<SuperAdminVitrine />} />
                    <Route path="partners" element={<SuperAdminPartners />} />
                    <Route path="condominios" element={<SuperAdminCondominios />} />
                    <Route path="comp-accounts" element={<SuperAdminCompAccounts />} />
                    <Route path="tutoriais" element={<SuperAdminTutoriais />} />
                    <Route path="white-label" element={<SuperAdminWhiteLabel />} />
                  </Route>
                  <Route path="/alugar/:slug" element={<AlugarPublico />} />
                  <Route path="/api/docs" element={<Suspense fallback={<PageLoader />}><ApiDocs /></Suspense>} />
                  <Route path="/r/:code" element={<ReferralRedirect />} />
                  <Route path="/portal-inquilino/:token" element={<Suspense fallback={<PageLoader />}><PortalInquilino /></Suspense>} />
                  <Route path="/parceiros/cadastro" element={<ParceirosCadastro />} />
                  {/* Página pública do anúncio: canal de aquisição orgânica, sem login. */}
                  <Route path="/vitrine/:categoria/:slug" element={<VitrineDetalhe />} />
                  {/* A lista, idem. Vem DEPOIS da rota de detalhe de propósito:
                      `/vitrine/servicos/algum-slug` tem de casar com aquela, não
                      com esta. */}
                  <Route path="/vitrine" element={<VitrineRoute />} />
                  <Route path="/" element={<HomeRoute />} />
                  {/* Preço é a informação que o visitante mais procura, e ela
                      estava atrás do login: "Ver planos" na página de vendas
                      levava à tela de entrar. Mesma tela, sem sessão. */}
                  <Route path="/precos" element={<Suspense fallback={<PageLoader />}><PlanosV3 /></Suspense>} />
                  <Route path="/*" element={<AppRoutes />} />
                </Routes>
              </Suspense>
              </ErrorBoundary>
            </SubscriptionProvider>
            </ActivePanelProvider>
          </RoleProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
